(function () {
  const STORAGE_KEY = 'dairymind-pricing-context';
  const PLAN_KEY = 'dairymind-combine-state';
  let presentmentCurrency = '';

  function money(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    const rounded = Math.round(Number(value || 0) * 100) / 100;
    const currency = presentmentCurrency || window.Shopify?.currency?.active;
    if (!currency) return rounded.toFixed(rounded % 1 === 0 ? 0 : 2);
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: rounded % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(rounded);
  }

  function positiveInteger(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function readStored(key) {
    try {
      return JSON.parse(window.sessionStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeStored(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage can be unavailable; the configurator still works for this page view.
    }
  }

  function syncOrderQuantityToUrl(orderQuantity) {
    const url = new URL(window.location.href);
    url.searchParams.set('order_quantity', String(orderQuantity));
    window.history.replaceState(window.history.state, '', url.toString());
  }

  function requestCart(path, options) {
    return fetch(window.Shopify.routes.root + path, options || {}).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Shopify cart request failed');
        }
        return data;
      });
    });
  }

  function setText(root, selector, value) {
    root.querySelectorAll(selector).forEach(function (node) {
      node.textContent = value;
    });
  }

  function variantId(product) {
    return product && product.variantId ? Number(product.variantId) : null;
  }

  function firstSellingPlan(product) {
    const plans = product && Array.isArray(product.sellingPlans) ? product.sellingPlans : [];
    return plans.length ? plans[0].id : null;
  }

  function hasDiscount(item) {
    return Boolean(
      item &&
      (
        (Array.isArray(item.discounts) && item.discounts.length) ||
        (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length) ||
        Number(item.discounted_price) < Number(item.original_price)
      )
    );
  }

  function gatewayTierCopy(pricing) {
    if (!pricing.meetsMinimum) return 'Minimum order: 20 MooCollars.';
    if (pricing.ownership.gatewayPrice === 0) return 'Gateway included when you order 40 or more MooCollars';
    return 'Gateway 50% off for an order of 20 MooCollars';
  }

  function ownershipNextStepCopy(pricing) {
    if (!pricing.meetsMinimum) return 'Enter 20 or more MooCollars to continue.';
    if (pricing.flexEligible) {
      return 'Save ' + money(pricing.comparisons.ownershipVsFlex) + ' in total compared with the Monthly Plan';
    }
    if (pricing.thresholds.nextGatewayDiscount > 0) {
      return 'Gateway: ' + money(pricing.ownership.gatewayPrice) + '. Add ' + pricing.thresholds.nextGatewayDiscount + ' more collars to unlock Gateway 50% off.';
    }
    return 'Gateway: ' + money(pricing.ownership.gatewayPrice) + '. Add ' + pricing.thresholds.nextGatewayIncluded + ' more collars to get your Gateway included and unlock the Monthly Plan.';
  }

  function ownershipOnlyStepCopy(pricing) {
    if (!pricing.meetsMinimum) return 'Enter 20 or more MooCollars to continue.';
    if (pricing.ownership.gatewayPrice === 0) return 'Gateway included when you order 40 or more MooCollars.';
    return 'Gateway 50% off for an order of 20 MooCollars.';
  }

  function readInitialContext(root, engine, selectableMinimum) {
    const params = new URLSearchParams(window.location.search);
    const local = readStored(PLAN_KEY) || {};
    const urlHerdSize = positiveInteger(params.get('herd_size'));
    const hasHerdContext = Boolean(urlHerdSize);
    const herdSize = hasHerdContext ? urlHerdSize : 0;
    const recommendedQuantity = hasHerdContext
      ? engine.normalizeCollarQuantity(herdSize)
      : selectableMinimum;
    const defaultOrderQuantity = engine.normalizeCollarQuantity(root.dataset.defaultHerdSize);
    const requestedOrderQuantity = positiveInteger(params.get('order_quantity'));
    const initialOrderQuantity = requestedOrderQuantity || (hasHerdContext ? recommendedQuantity : defaultOrderQuantity);
    const orderQuantity = engine.isValidCollarQuantity(initialOrderQuantity) && initialOrderQuantity >= selectableMinimum
      ? initialOrderQuantity
      : recommendedQuantity;
    return {
      herdSize: herdSize,
      hasHerdContext: hasHerdContext,
      orderQuantity: orderQuantity,
      farmType: params.get('farm_type') || '',
      activePlan: local.activePlan === 'flex' ? 'flex' : 'ownership'
    };
  }

  function initialize(root) {
    const configRoot = root;
    const billingTiming = configRoot.dataset.flexBillingTiming || 'unknown';
    const ownershipOnly = configRoot.dataset.combineMode === 'ownership';
    const combineUrl = configRoot.dataset.combineUrl || '/pages/combine';
    const planName = configRoot.dataset.planName || 'Ownership';
    const oneTimePlanName = configRoot.dataset.oneTimePlanName || planName;
    const monthlyPlanName = configRoot.dataset.monthlyPlanName || 'Flex';
    const includedLabel = configRoot.dataset.includedLabel || 'Included';
    const minimumOrderText = configRoot.dataset.minimumOrderText || 'Minimum order: 20 MooCollars.';
    const monthlyTermLabel = configRoot.dataset.monthlyTermLabel || '12 months';
    const flexDueNote = configRoot.dataset.flexDueNote || 'Includes first monthly DairyMind payment';
    const flexHardwareOnlyNote = configRoot.dataset.flexHardwareOnlyNote || 'Estimated hardware due today only. Software checkout timing is not yet connected.';
    const noRecurringFeeLabel = configRoot.dataset.noRecurringFeeLabel || 'No recurring software fee';
    const commerce = JSON.parse(configRoot.querySelector('[data-combine-commerce]')?.textContent || '{}');
    presentmentCurrency = commerce.currency || window.Shopify?.currency?.active || '';
    const engine = window.DairyMindPricingEngine?.create({ commerce: commerce });
    if (!engine) return;

    const selectableMinimum = ownershipOnly ? engine.constants.MIN_QUANTITY : engine.constants.FLEX_MIN_QUANTITY;
    const state = readInitialContext(configRoot, engine, selectableMinimum);
    if (ownershipOnly) state.activePlan = 'ownership';
    root = document;
    const input = root.querySelector('[data-combine-order-input]');
    const status = root.querySelector('[data-combine-status]');
    const validation = root.querySelector('[data-combine-validation]');
    const checkoutStatus = root.querySelector('[data-combine-checkout-status]');
    const sticky = root.querySelector('[data-combine-sticky]');
    const minusButton = root.querySelector('[data-combine-order-step="-1"]');
    const herdContext = root.querySelector('[data-combine-herd-context]');

    function pricing() {
      return engine.calculate({
        herdSize: state.herdSize,
        orderQuantity: state.orderQuantity
      }, billingTiming);
    }

    function persist() {
      writeStored(PLAN_KEY, {
        herdSize: state.hasHerdContext ? state.herdSize : null,
        hasHerdContext: state.hasHerdContext,
        orderQuantity: state.orderQuantity,
        farmType: state.farmType,
        activePlan: state.activePlan
      });
      writeStored(STORAGE_KEY, Object.assign({}, readStored(STORAGE_KEY) || {}, {
        herdSize: state.hasHerdContext ? state.herdSize : null,
        orderQuantity: state.orderQuantity,
        farmType: state.farmType
      }));
    }

    function setPlan(plan) {
      const current = pricing();
      state.activePlan = !ownershipOnly && plan === 'flex' && current.flexEligible ? 'flex' : 'ownership';
      persist();
      render();
    }

    function setButtonState(button, disabled) {
      if (!button) return;
      button.disabled = disabled;
      button.classList.toggle('dm-combine__button-disabled', disabled);
    }

    function render() {
      const current = pricing();
      if ((ownershipOnly || !current.flexEligible) && state.activePlan === 'flex') state.activePlan = 'ownership';
      const benchmarkTotal = (current.orderQuantity * current.constants.COMPETITOR_COLLAR_BENCHMARK) + current.constants.GATEWAY_STANDARD_PRICE;
      const ownershipEstimatedSavings = Math.max(0, benchmarkTotal - current.ownership.total);
      const flexEstimatedSavings = current.flexEligible ? Math.max(0, benchmarkTotal - current.flex.twelveMonthTotal) : 0;

      if (input) input.value = String(current.orderQuantity || current.recommendedMinimum);
      if (minusButton) minusButton.disabled = current.orderQuantity <= selectableMinimum;
      if (herdContext) herdContext.hidden = !state.hasHerdContext;

      if (state.hasHerdContext) setText(root, '[data-combine-actual-herd]', String(current.herdSize));
      setText(root, '[data-combine-order-display]', String(current.orderQuantity || current.minimumQuantity));
      setText(root, '[data-combine-ownership-unit-price]', money(current.ownership.collarPrice));
      setText(root, '[data-combine-flex-unit-price]', money(current.flex.collarPrice));
      setText(root, '[data-combine-software-unit-price]', money(current.flex.softwareMonthlyPerCow));
      setText(root, '[data-combine-ownership-collars]', money(current.ownership.collarSubtotal));
      setText(root, '[data-combine-ownership-total]', money(current.ownership.total));
      setText(root, '[data-combine-ownership-gateway-price]', current.ownership.gatewayPrice === 0 ? includedLabel : money(current.ownership.gatewayPrice));
      setText(root, '[data-combine-ownership-gateway-note]', gatewayTierCopy(current));
      setText(root, '[data-combine-ownership-gateway-bullet]', gatewayTierCopy(current));
      setText(root, '[data-combine-flex-hardware]', money(current.flex.collarSubtotal));
      setText(root, '[data-combine-flex-monthly]', money(current.flex.monthlySoftwareFee));
      setText(root, '[data-combine-flex-gateway-price]', money(current.flex.gatewayPrice));
      setText(root, '[data-combine-flex-due]', money(current.flex.estimatedDueToday));
      setText(root, '[data-combine-flex-12]', money(current.flex.twelveMonthTotal));
      setText(root, '[data-combine-ownership-vs-flex]', money(current.comparisons.ownershipVsFlex));
      setText(root, '[data-combine-flex-upfront-reduction]', money(current.comparisons.flexUpfrontReduction));
      setText(root, '[data-combine-ownership-benchmark]', money(ownershipEstimatedSavings));
      setText(root, '[data-combine-flex-benchmark]', money(flexEstimatedSavings));
      setText(root, '[data-combine-ownership-compare]', ownershipOnly ? ownershipOnlyStepCopy(current) : ownershipNextStepCopy(current));

      if (state.activePlan === 'ownership') {
        setText(root, '[data-combine-strip-moocollar]', money(current.ownership.collarPrice) + ' / collar');
        setText(root, '[data-combine-strip-dairymind]', includedLabel);
        setText(root, '[data-combine-strip-gateway]', current.ownership.gatewayPrice === 0 ? includedLabel : money(current.ownership.gatewayPrice));
        setText(root, '[data-combine-strip-herd]', oneTimePlanName);
      } else {
        setText(root, '[data-combine-strip-moocollar]', money(current.flex.collarPrice) + ' / collar');
        setText(root, '[data-combine-strip-dairymind]', money(current.flex.softwareMonthlyPerCow) + ' / cow / month');
        setText(root, '[data-combine-strip-gateway]', money(current.flex.gatewayPrice) + ' · 50% off');
        setText(root, '[data-combine-strip-herd]', monthlyPlanName + ' · ' + monthlyTermLabel);
      }

      if (current.flexEligible) {
        setText(root, '[data-combine-flex-compare]', 'Save ' + money(current.comparisons.flexUpfrontReduction) + ' upfront compared with the One-Time Purchase');
      } else {
        setText(root, '[data-combine-flex-compare]', 'The Monthly Plan is available for orders of 40 or more MooCollars.');
      }

      const dueNote = root.querySelector('[data-combine-flex-due-note]');
      const thenNode = root.querySelector('[data-combine-flex-then]');
      if (billingTiming === 'first_month_at_checkout') {
        if (dueNote) dueNote.textContent = flexDueNote;
        if (thenNode) thenNode.textContent = money(current.flex.monthlySoftwareFee) + ' / month for 11 more months';
      } else {
        if (dueNote) dueNote.textContent = flexHardwareOnlyNote;
        if (thenNode) thenNode.textContent = money(current.flex.monthlySoftwareFee) + ' / month for 12 months';
      }

      root.querySelectorAll('[data-combine-plan-tab]').forEach(function (tab) {
        const isFlex = tab.dataset.combinePlanTab === 'flex';
        const selected = tab.dataset.combinePlanTab === state.activePlan;
        tab.setAttribute('aria-selected', String(selected));
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        tab.disabled = isFlex && !current.flexEligible;
      });

      root.querySelectorAll('[data-combine-plan-panel]').forEach(function (panel) {
        const active = panel.dataset.combinePlanPanel === state.activePlan;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
        panel.classList.toggle('is-active', active);
      });

      root.querySelectorAll('[data-combine-compare-card]').forEach(function (card) {
        const isFlex = card.dataset.combineCompareCard === 'flex';
        card.hidden = isFlex && !current.flexEligible;
        card.classList.toggle('is-active', card.dataset.combineCompareCard === state.activePlan);
      });

      const ownershipCompareCardCopy = root.querySelector('[data-combine-ownership-compare-card-copy]');
      if (ownershipCompareCardCopy) ownershipCompareCardCopy.hidden = !current.flexEligible;

      if (status) {
        if (!current.meetsMinimum) {
          status.textContent = minimumOrderText;
          status.hidden = false;
        } else if (ownershipOnly && current.flexEligible) {
          status.innerHTML = 'For orders of 40 or more MooCollars, you can also compare the Monthly Plan on the <a href="' + combineUrl + '">Combine Plan page</a>.';
          status.hidden = false;
        } else if (ownershipOnly) {
          status.textContent = '';
          status.hidden = true;
        } else if (current.flexEligible) {
          status.textContent = '';
          status.hidden = true;
        } else {
          status.textContent = 'The Monthly Plan is available for orders of 40 or more MooCollars. Add ' + current.thresholds.flexUnlockRemaining + ' more collars to unlock it.';
          status.hidden = false;
        }
      }

      if (validation) {
        validation.textContent = current.meetsMinimum ? '' : 'Please enter 20 or more MooCollars to continue.';
      }

      const activePrice = state.activePlan === 'ownership' ? current.ownership.total : current.flex.estimatedDueToday;
      const ownershipSupport = current.ownership.gatewayPrice === 0 ? noRecurringFeeLabel : gatewayTierCopy(current);
      const activeSupport = state.activePlan === 'ownership'
        ? ownershipSupport
        : billingTiming === 'first_month_at_checkout'
          ? 'Then ' + money(current.flex.monthlySoftwareFee) + '/month · 12-month commitment'
          : money(current.flex.monthlySoftwareFee) + '/month for 12 months · billing timing to confirm';
      const stickyContext = state.hasHerdContext
        ? current.herdSize + '-cow herd · ' + current.orderQuantity + ' collars'
        : current.orderQuantity + ' collars';
      setText(root, '[data-combine-sticky-title]', (state.activePlan === 'ownership' ? oneTimePlanName : monthlyPlanName) + ' · ' + stickyContext);
      setText(root, '[data-combine-sticky-price]', money(activePrice) + ' due today');
      setText(root, '[data-combine-sticky-support]', activeSupport);

      root.querySelectorAll('[data-combine-continue], [data-combine-sticky-continue]').forEach(function (button) {
        const buttonPlan = button.dataset.combineContinue || state.activePlan;
        setButtonState(button, !current.meetsMinimum || (buttonPlan === 'flex' && !current.flexEligible));
      });
    }

    function setOrderQuantity(value) {
      state.orderQuantity = Math.max(selectableMinimum, engine.normalizeCollarQuantity(value));
      const current = pricing();
      if ((ownershipOnly || !current.flexEligible) && state.activePlan === 'flex') state.activePlan = 'ownership';
      persist();
      syncOrderQuantityToUrl(state.orderQuantity);
      render();
    }

    function clearCart() {
      return requestCart('cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      });
    }

    function addConfiguredItems(items, attributes) {
      return clearCart()
        .then(function () {
          return requestCart('cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          });
        })
        .then(function () {
          return requestCart('cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: attributes })
          });
        })
        .then(function () {
          return requestCart('cart.js');
        });
    }

    function findCartItems(cart, id, model) {
      return (cart.items || []).filter(function (item) {
        return Number(item.variant_id) === Number(id) && item.properties && item.properties._commercial_model === model;
      });
    }

    function cartLineQuantity(lines) {
      return lines.reduce(function (total, line) {
        return total + Number(line.quantity || 0);
      }, 0);
    }

    function verifyCart(cart, plan, current, ids) {
      const model = plan === 'flex' ? 'COMBINE_FLEX' : 'COMBINE_OWNERSHIP';
      const wrongModel = plan === 'flex' ? 'COMBINE_OWNERSHIP' : 'COMBINE_FLEX';
      const wrongCollarId = plan === 'flex' ? variantId(commerce.ownershipProduct) : variantId(commerce.flexHardwareProduct);
      const collarLines = findCartItems(cart, ids.collar, model);
      const gatewayLines = findCartItems(cart, ids.gateway, model);
      const wrongCollarLines = wrongCollarId ? findCartItems(cart, wrongCollarId, wrongModel) : [];
      const gateway = gatewayLines[0];
      const errors = [];
      const collarQuantity = cartLineQuantity(collarLines);
      const wrongCollarQuantity = cartLineQuantity(wrongCollarLines);
      const gatewayQuantity = cartLineQuantity(gatewayLines);

      if (collarQuantity !== Number(current.orderQuantity)) {
        errors.push('MooCollar cart quantity does not match the selected collar quantity.');
      }
      if (wrongCollarQuantity > 0) {
        errors.push('The cart contains MooCollars from another purchase plan. Please try again so we can rebuild the current plan cleanly.');
      }
      if (gatewayQuantity !== 1) {
        errors.push('Gateway quantity should be 1.');
      }

      if (plan === 'flex') {
        const softwareLines = findCartItems(cart, ids.software, model);
        const softwareQuantity = cartLineQuantity(softwareLines);
        if (softwareQuantity !== Number(current.orderQuantity)) errors.push('DairyMind software quantity does not match the selected collar quantity.');
        if (softwareLines.some(function (line) { return !line.selling_plan_allocation; })) errors.push('DairyMind software selling plan is missing from the cart line.');
        if (gateway && !hasDiscount(gateway)) errors.push('Gateway automatic discount was not detected for Flex.');
      } else if (gateway && current.ownership.gatewaySavings > 0 && !hasDiscount(gateway)) {
        errors.push('Expected Gateway automatic discount was not detected for Ownership.');
      }

      return errors;
    }

    function buildCartRequest(plan, current) {
      const gatewayId = variantId(commerce.gatewayProduct);
      const herdSizeProperty = state.hasHerdContext ? String(current.herdSize) : '';
      if (!gatewayId) throw new Error('Gateway product is not mapped. Select the Gateway product in this section settings.');

      if (plan === 'ownership') {
        const collarId = variantId(commerce.ownershipProduct);
        if (!collarId) throw new Error('Ownership MooCollar product is not mapped. Select the Ownership MooCollar product in this section settings.');
        return {
          ids: { collar: collarId, gateway: gatewayId },
          items: [
            {
              id: collarId,
              quantity: current.orderQuantity,
              properties: { _commercial_model: 'COMBINE_OWNERSHIP', herd_size: herdSizeProperty, order_quantity: String(current.orderQuantity), farm_type: state.farmType }
            },
            {
              id: gatewayId,
              quantity: 1,
              properties: { _commercial_model: 'COMBINE_OWNERSHIP', herd_size: herdSizeProperty, order_quantity: String(current.orderQuantity), gateway_tier: gatewayTierCopy(current) }
            }
          ],
          attributes: {
            commercial_model: 'COMBINE_OWNERSHIP',
            herd_size: herdSizeProperty,
            order_quantity: String(current.orderQuantity),
            farm_type: state.farmType
          }
        };
      }

      if (!current.flexEligible) throw new Error('The Monthly Plan is available for orders of 40 or more MooCollars.');
      const collarId = variantId(commerce.flexHardwareProduct);
      const softwareId = variantId(commerce.flexSoftwareProduct);
      const sellingPlanId = firstSellingPlan(commerce.flexSoftwareProduct);
      if (!collarId) throw new Error('Flex MooCollar product is not mapped. Select the Flex MooCollar hardware product in this section settings.');
      if (!softwareId) throw new Error('Flex DairyMind software product is not mapped. Select the Flex DairyMind software product in this section settings.');
      if (!sellingPlanId) throw new Error('Flex DairyMind software selling plan is not available on the selected product. Configure the selling plan before enabling checkout.');

      return {
        ids: { collar: collarId, gateway: gatewayId, software: softwareId },
        items: [
          {
            id: collarId,
            quantity: current.orderQuantity,
            properties: { _commercial_model: 'COMBINE_FLEX', herd_size: herdSizeProperty, order_quantity: String(current.orderQuantity), farm_type: state.farmType }
          },
          {
            id: gatewayId,
            quantity: 1,
            properties: { _commercial_model: 'COMBINE_FLEX', herd_size: herdSizeProperty, order_quantity: String(current.orderQuantity), gateway_offer: '50% off standard Gateway price' }
          },
          {
            id: softwareId,
            quantity: current.orderQuantity,
            selling_plan: sellingPlanId,
            properties: { _commercial_model: 'COMBINE_FLEX', herd_size: herdSizeProperty, order_quantity: String(current.orderQuantity), commitment: '12 months' }
          }
        ],
        attributes: {
          commercial_model: 'COMBINE_FLEX',
          herd_size: herdSizeProperty,
          order_quantity: String(current.orderQuantity),
          farm_type: state.farmType
        }
      };
    }

    function continuePlan(plan) {
      const current = pricing();
      const selected = !ownershipOnly && plan === 'flex' && current.flexEligible ? 'flex' : 'ownership';
      if (!current.meetsMinimum) {
        if (checkoutStatus) checkoutStatus.textContent = 'Minimum order: 20 MooCollars. Please increase quantity before continuing.';
        return;
      }

      let request;
      try {
        request = buildCartRequest(selected, current);
      } catch (error) {
        if (checkoutStatus) checkoutStatus.textContent = error.message;
        console.error('[DairyMind combine checkout]', error);
        return;
      }

      if (checkoutStatus) checkoutStatus.textContent = 'Preparing your cart...';
      addConfiguredItems(request.items, request.attributes)
        .then(function (cart) {
          const errors = verifyCart(cart, selected, current, request.ids);
          if (errors.length) {
            const error = new Error(errors.join(' '));
            error.cart = cart;
            throw error;
          }
          window.location.assign(window.Shopify.routes.root + 'checkout');
        })
        .catch(function (error) {
          if (checkoutStatus) checkoutStatus.textContent = error.message || 'We could not prepare the cart. Please try again or contact our team.';
          console.error('[DairyMind combine checkout]', error);
        });
    }

    root.querySelectorAll('[data-combine-plan-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        setPlan(tab.dataset.combinePlanTab);
      });
      tab.addEventListener('keydown', function (event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const tabs = Array.from(root.querySelectorAll('[data-combine-plan-tab]')).filter(function (item) {
          return !item.disabled;
        });
        const currentIndex = tabs.indexOf(tab);
        let nextIndex = currentIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        event.preventDefault();
        tabs[nextIndex]?.focus();
        if (tabs[nextIndex]) setPlan(tabs[nextIndex].dataset.combinePlanTab);
      });
    });

    root.querySelectorAll('[data-combine-plan-jump]').forEach(function (button) {
      button.addEventListener('click', function () {
        setPlan(button.dataset.combinePlanJump);
        root.querySelector('#combine-configurator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    root.querySelectorAll('[data-combine-continue]').forEach(function (button) {
      button.addEventListener('click', function () {
        continuePlan(button.dataset.combineContinue);
      });
    });
    root.querySelector('[data-combine-sticky-continue]')?.addEventListener('click', function () {
      continuePlan(state.activePlan);
    });
    root.querySelector('[data-combine-scroll-top]')?.addEventListener('click', function () {
      root.querySelector('#combine-configurator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    root.querySelectorAll('[data-combine-order-step]').forEach(function (button) {
      button.addEventListener('click', function () {
        const direction = Number(button.dataset.combineOrderStep);
        setOrderQuantity(state.orderQuantity + (direction * engine.constants.COLLAR_QUANTITY_INCREMENT));
      });
    });
    input.addEventListener('input', function () {
      input.value = input.value.replace(/[^\d]/g, '');
    });
    input.addEventListener('change', function () {
      setOrderQuantity(input.value);
    });
    root.querySelectorAll('[data-combine-media]').forEach(function (button) {
      button.addEventListener('click', function () {
        root.querySelectorAll('[data-combine-media]').forEach(function (item) {
          item.classList.toggle('is-active', item === button);
        });
      });
    });

    if ('IntersectionObserver' in window && sticky) {
      const hero = root.querySelector('#combine-configurator');
      new IntersectionObserver(function (entries) {
        sticky.hidden = entries[0].isIntersecting;
      }, { threshold: 0.05 }).observe(hero);
    }

    render();
    window.DairyMindCommercial?.emit('commercial_landing_view', {
      commercial_model: 'COMBINE_PAGE',
      herd_size: state.hasHerdContext ? state.herdSize : '',
      order_quantity: state.orderQuantity,
      farm_type: state.farmType,
      ownership_variant_id: commerce.ownershipProduct?.variantId || '',
      flex_hardware_variant_id: commerce.flexHardwareProduct?.variantId || '',
      flex_software_variant_id: commerce.flexSoftwareProduct?.variantId || '',
      gateway_variant_id: commerce.gatewayProduct?.variantId || ''
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-dm-combine-page]').forEach(initialize);
  });
})();
